import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
  LinkOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Modal, Popover, Row, Spin, Table, Tag, Tooltip } from "antd";
import { FormInstance } from "antd/lib/form";
import { ColumnType } from "antd/lib/table";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import BaseForm from "../components/Form/BaseForm";
import {
  CustomerLien,
  FormFieldConfig,
  Lien,
  SelectOptionsInterface,
} from "../helpers/shared-interfaces";
import { formatter, getStateColor, injectKey } from "../helpers/utilities";
import ModalCreator from "./ModalCreator";
import { NavLink } from "react-router-dom";
import { ChangelogTableFactory } from "../pages/ViewCustomer";

interface Props {
  showHeading: boolean;
  liens: CustomerLien[];
  createLien: (loanPaid?: boolean) => Promise<void>;
  updateLien: (lienId: string, loanPaid?: boolean) => Promise<void>;
  deleteLien: (lienId: string | undefined) => Promise<void>;
  handleFileUploadChange: (field: string, combinedInfo: string) => void;
  reference: RefObject<FormInstance>;
  showForm: boolean;
  isLoading: boolean;
  clients?: SelectOptionsInterface[];
}

interface State {
  liens: Lien[];
  loanPaid: boolean;
  selectedLien: CustomerLien | undefined;
  showDeleteModal: boolean;
  isShowDeleteLoading: boolean;
  showFormModal: boolean;
  showAuditDrawer: boolean;
  selectedLienForAudit: CustomerLien | undefined;
}

export const generateNoteFormFields = ({
  createLien,
  loanPaid,
  changeLoanPaid,
  selectedLien,
  updateLien,
  handleFileUploadChange,
  clients,
}: {
  createLien: (loanPaid: boolean) => void;
  loanPaid: boolean;
  changeLoanPaid: (checked: boolean) => void;
  selectedLien: CustomerLien | undefined;
  updateLien: (lienId: string, loanPaid: boolean) => void;
  handleFileUploadChange: (field: string, combinedInfo: string) => void;
  clients?: SelectOptionsInterface[];
}): FormFieldConfig[] => {
  return [
    {
      id: "currentAmount",
      name: "currentAmount",
      required: true,
      inputType: "number",
      rule: "number",
      label: "Current Amount",
      placeholder: "Amount",
      initialValue: selectedLien?.currentAmount,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
      },
    },
    {
      id: "totalAmount",
      name: "totalAmount",
      required: true,
      inputType: "number",
      rule: "number",
      label: "Total Amount",
      placeholder: "Total Amount",
      initialValue: selectedLien?.totalAmount,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
        offset: 1,
      },
    },
    {
      id: "provider",
      name: "provider",
      required: true,
      inputType: "string",
      rule: "string",
      label: "Provider",
      placeholder: "Provider",
      initialValue: selectedLien?.provider,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
        offset: 1,
      },
    },
    {
      id: "employeePreTax",
      name: "employeePreTax",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Employee Pre Tax",
      placeholder: "Employee Pre Tax",
      initialValue: selectedLien?.employeePreTax ?? 0,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
      },
    },
    {
      id: "employeePostTax",
      name: "employeePostTax",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Employee Post Tax",
      placeholder: "Employee Post Tax",
      initialValue: selectedLien?.employeePostTax ?? 0,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
        offset: 1,
      },
    },
    {
      id: "employerPreTax",
      name: "employerPreTax",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Employer Pre Tax",
      placeholder: "Employer Pre Tax",
      initialValue: selectedLien?.employerPreTax ?? 0,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
        offset: 1,
      },
    },
    {
      id: "employerPostTax",
      name: "employerPostTax",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Employer Post Tax",
      placeholder: "Employer Post Tax",
      initialValue: selectedLien?.employerPostTax ?? 0,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
      },
    },
    {
      id: "loanPaid",
      name: "loanPaid",
      required: false,
      inputType: "checkbox",
      rule: "boolean",
      label: "Loan Paid ?",
      checkboxCheckedValue: loanPaid,
      checkboxChangeHandler: (e) => changeLoanPaid(e.target.checked),
      elementStyle: { width: "100%" },
      colConfig: {
        span: 6,
        offset: 1,
      },
    },
    {
      id: "startDate",
      name: "startDate",
      inputType: "defaultDate",
      required: true,
      rule: "object",
      label: "Start Date",
      elementStyle: { width: "100%" },
      initialValue: selectedLien?.startDate
        ? moment(selectedLien.startDate)
        : undefined,
      colConfig: {
        span: 6,
        offset: 1,
      },
    },
    {
      id: "endDate",
      name: "endDate",
      inputType: "defaultDate",
      required: true,
      rule: "object",
      label: "End Date",
      elementStyle: { width: "100%" },
      initialValue: selectedLien?.endDate
        ? moment(selectedLien?.endDate)
        : undefined,
      colConfig: {
        span: 6,
      },
    },
    
    {
      id: "companyId",
      name: "companyId",
      placeholder: "Select Client",
      required: true,
      inputType: "select",
      rule: "string",
      label: "Select Client",
      initialValue: selectedLien?.company?.id,
      selectOptions: clients,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 6,
        offset: 1,
      },
    },
    {
      name: "lienAttachmentUrl",
      label: "Upload Document",
      inputType: "fileUpload",
      defaultFileList: [],
      placeholder: "Upload Document",
      rule: "string",
      required: true,
      changeHandler: (combinedInfo: string) =>
        handleFileUploadChange("lienAttachmentUrl", combinedInfo),
      colConfig: {
        offset: 1,
        span: 7,
      },
    },
    {
      name: "createLienButton",
      value: `${selectedLien ? "Update" : "Create"} Lien`,
      inputType: "button",
      required: false,
      elementStyle: {
        position: "relative",
        borderRadius: 0,
        height: "33px",
      },
      className: "base-button",
      handleClick: () =>
        selectedLien
          ? updateLien(selectedLien.id, loanPaid)
          : createLien(loanPaid),
      colConfig: {
        span: 4,
      },
    },
  ];
};

export function getColumns<T extends CustomerLien = CustomerLien>(
  toggleShowDeleteModal: (lien: CustomerLien | undefined) => void,
  selectLien: (lien: CustomerLien) => void,
  openAuditDrawer: (lien: CustomerLien) => void,
): ColumnType<T>[] {
  return [
    {
      title: "Loan Request",
      dataIndex: "loanRequest",
      key: "loanRequest",
      width: "10%",
      align: "center",
      render(text: any, record: CustomerLien): JSX.Element {
        return record.loanRequest ? (
          <NavLink to={`/loan-requests/${record.loanRequest.id}`}>
            <EyeOutlined />
          </NavLink>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      width: "12%",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Client",
      dataIndex: "companyName",
      key: "companyName",
      width: "12%",
      render(text: string, record: CustomerLien): JSX.Element {
        return <span>{record.company?.name}</span>;
      },
    },
    {
      title: "Current Amount",
      dataIndex: "currentAmount",
      key: "currentAmount",
      width: "10%",
      render(text: number): JSX.Element {
        return <span>{formatter.format(text)}</span>;
      },
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: "10%",
      render(text: number): JSX.Element {
        return <span>{formatter.format(text)}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "loanPaid",
      key: "loanPaid",
      width: "8%",
      align: "center",
      render(text: boolean, record: CustomerLien): JSX.Element {
        return (
          <Tag color={getStateColor(text ? "paid" : "not_paid")} key={record.id}>
            {text ? "Paid" : "Not Paid"}
          </Tag>
        );
      },
    },
    {
      title: "Tax Breakdown",
      key: "taxBreakdown",
      width: "8%",
      align: "center" as const,
      render(text: string, record: CustomerLien): JSX.Element {
        const content = (
          <div style={{ fontSize: "12px" }}>
            <div>
              <strong>Emp. Pre-Tax:</strong>{" "}
              {formatter.format(record.employeePreTax)}
            </div>
            <div>
              <strong>Emp. Post-Tax:</strong>{" "}
              {formatter.format(record.employeePostTax)}
            </div>
            <div>
              <strong>Empr. Pre-Tax:</strong>{" "}
              {formatter.format(record.employerPreTax)}
            </div>
            <div>
              <strong>Empr. Post-Tax:</strong>{" "}
              {formatter.format(record.employerPostTax)}
            </div>
          </div>
        );
        return (
          <Popover content={content} title="Tax Breakdown" trigger="hover">
            <Button type="link" size="small">
              View
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "Dates",
      key: "dates",
      width: "14%",
      render(text: string, record: CustomerLien): JSX.Element {
        return (
          <div style={{ fontSize: "12px" }}>
            {record.startDate && (
              <div>
                <strong>Start:</strong>{" "}
                {moment(record.startDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
              </div>
            )}
            {record.endDate && (
              <div>
                <strong>End:</strong>{" "}
                {moment(record.endDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Attachment",
      dataIndex: "attachment",
      key: "attachment",
      width: "8%",
      align: "center" as const,
      render(text: string, record: CustomerLien): JSX.Element {
        return record.lienAttachmentUrl ? (
          <a
            href={record.lienAttachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkOutlined style={{ fontSize: "16px" }} />
          </a>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      width: "8%",
      align: "center" as const,
      fixed: "right" as const,
      render(text: string, record: CustomerLien): JSX.Element {
        return (
          <div style={{ display: "flex", alignItems: "center", flexDirection: "column", gap: "6px" }}>
            <Tooltip title="Edit Lien">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => selectLien(record)}
                size="small"
              />
            </Tooltip>
            <Tooltip title="View Audit History">
              <Button
                icon={<HistoryOutlined />}
                onClick={() => openAuditDrawer(record)}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Delete Lien">
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => toggleShowDeleteModal(record)}
                size="small"
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];
}

export default class CustomerLienSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      liens: [],
      loanPaid: false,
      selectedLien: undefined,
      showDeleteModal: false,
      isShowDeleteLoading: false,
      showFormModal: false,
      showAuditDrawer: false,
      selectedLienForAudit: undefined,
    };
  }

  changeLoanPaid = async (checkedValue: boolean) => {
    this.setState({
      loanPaid: checkedValue,
    });
  };

  selectLien = (lien: CustomerLien): void => {
    this.setState({
      selectedLien: lien,
      loanPaid: lien.loanPaid ?? false,
      showFormModal: true,
    });
  };

  openFormModalForCreate = (): void => {
    this.setState({
      selectedLien: undefined,
      loanPaid: false,
      showFormModal: true,
    });
  };

  closeFormModal = (): void => {
    this.setState({
      showFormModal: false,
      selectedLien: undefined,
      loanPaid: false,
    });
  };

  createLienWrapper = (loanPaid?: boolean): void => {
    this.props.createLien(loanPaid);
    this.setState({ showFormModal: false });
  };

  toggleShowDeleteModal = (lien: CustomerLien | undefined): void => {
    let selectedLien: CustomerLien | undefined = undefined;
    if (lien !== undefined) {
      selectedLien = lien;
    }
    this.setState((previousState: Readonly<State>) => ({
      showDeleteModal: !previousState.showDeleteModal,
      selectedLien,
    }));
  };

  updateLienWrapper = (lienId: string, loanPaid?: boolean): void => {
    this.props.updateLien(lienId, loanPaid);
    this.setState({
      selectedLien: undefined,
      loanPaid: false,
      showFormModal: false,
    });
  };

  openAuditDrawer = (lien: CustomerLien): void => {
    this.setState({
      selectedLienForAudit: lien,
      showAuditDrawer: true,
    });
  };

  closeAuditDrawer = (): void => {
    this.setState({
      showAuditDrawer: false,
      selectedLienForAudit: undefined,
    });
  };

  render(): JSX.Element {
    const {
      showFormModal,
      showAuditDrawer,
      selectedLienForAudit,
      selectedLien,
    } = this.state;

    const lienFields = generateNoteFormFields({
      createLien: this.createLienWrapper,
      loanPaid: this.state.loanPaid,
      changeLoanPaid: this.changeLoanPaid,
      selectedLien,
      updateLien: this.updateLienWrapper,
      handleFileUploadChange: this.props.handleFileUploadChange,
      clients: this.props.clients,
    });

    return (
      <Spin spinning={this.props.isLoading}>
        <Fragment>
          {/* Delete confirmation modal */}
          {ModalCreator({
            title: "Delete Customer Lien",
            okType: "primary",
            okText: "Delete Customer Lien",
            showModal: this.state.showDeleteModal,
            isLoading: this.state.isShowDeleteLoading,
            onOkay: () => {
              this.props.deleteLien(this.state?.selectedLien?.id);
              this.toggleShowDeleteModal(undefined);
              this.setState({ selectedLien: undefined });
            },
            toggleModal: () => this.toggleShowDeleteModal(undefined),
            inscription: "Are you sure you want to delete this lien ?",
          })}

          {/* Create / Edit lien modal */}
          <Modal
            title={selectedLien ? "Edit Lien" : "Add Lien"}
            visible={showFormModal}
            onCancel={this.closeFormModal}
            footer={null}
            width={1200}
            maskClosable={false}
            destroyOnClose
          >
            <BaseForm
              layout="vertical"
              reference={this.props.reference}
              fields={lienFields}
              fieldsToHide={[]}
            />
          </Modal>

          {/* Audit history drawer */}
          <Drawer
            title={`Lien Audit History${selectedLienForAudit?.provider ? ` — ${selectedLienForAudit.provider}` : ""}`}
            width={900}
            visible={showAuditDrawer}
            onClose={this.closeAuditDrawer}
            maskClosable={false}
            bodyStyle={{ paddingBottom: 80 }}
          >
            {selectedLienForAudit && (
              ChangelogTableFactory(selectedLienForAudit.changeLogs || [])
            )}
          </Drawer>

          {/* Add Lien button */}
          {this.props.showForm && (
            <Row style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={this.openFormModalForCreate}
              >
                Add Lien
              </Button>
            </Row>
          )}

          {/* Lien table */}
          {this.props.liens.length > 0 && (
            <Table
              bordered={true}
              columns={getColumns(
                this.toggleShowDeleteModal,
                this.selectLien,
                this.openAuditDrawer,
              )}
              dataSource={injectKey(this.props.liens, "id")}
              pagination={false}
              scroll={{ x: true }}
            />
          )}
        </Fragment>
      </Spin>
    );
  }
}
